import React from "react";

import notification from "antd/es/notification";
import { getLocation, RouterLocation } from "connected-react-router";
import { IntlShape } from "react-intl";
import { all, call, fork, put, select, takeEvery, takeLatest } from "redux-saga/effects";
import * as uuid from "uuid";

import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import { EntityType } from "@mapmycustomers/shared/types/entity";
import Folder from "@mapmycustomers/shared/types/entity/Folder";
import Resource from "@mapmycustomers/shared/types/entity/Resource";
import { RawFile } from "@mapmycustomers/shared/types/File";
import Organization from "@mapmycustomers/shared/types/Organization";
import { ResourceResponse } from "@mapmycustomers/shared/types/resources/ResourceResponse";
import User from "@mapmycustomers/shared/types/User";
import { FilterModel } from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import ApiError from "@mapmycustomers/shared/util/api/ApiError";
import BulkApiError from "@mapmycustomers/shared/util/api/BulkApiError";
import { isApiError } from "@mapmycustomers/shared/util/assert";
import { Button } from "@mapmycustomers/ui";

import i18nService from "@app/config/I18nService";
import { callApi } from "@app/store/api/callApi";
import { handleError } from "@app/store/errors/actions";
import {
  getCurrentUserId,
  getOrganization,
  getOrganizationId,
  getPinnedResourceIds,
  isCurrentUserMember,
} from "@app/store/iam";
import { updateMetadata } from "@app/store/iam/actions";
import { notifyAboutChanges } from "@app/store/uiSync/actions";
import FileListItem from "@app/types/FileListItem";
import ResourcesCreatePayload from "@app/types/resources/ResourcesCreatePayload";
import { allSettled, SettleResult } from "@app/util/effects";
import getValidationErrors from "@app/util/errorHandling/getValidationErrors";
import resourceFieldModel, { ResourceFieldName } from "@app/util/fieldModel/ResourceFieldModel";
import {
  createSimpleCondition,
  getSimpleConditionForField,
  removeSimpleConditionForFields,
  upsertSimpleConditions,
} from "@app/util/filters/utils";
import { convertToPlatformSortModel } from "@app/util/viewModel/convertSort";
import { convertToPlatformFilterModel } from "@app/util/viewModel/convertToPlatformFilterModel";

import { STORAGE_UPGRADE_LINK, TRASH_FOLDER } from "../utils/const";

import {
  bulkCreateResources,
  bulkDeleteFolders,
  bulkDeleteResources,
  bulkEditFolders,
  bulkEditResources,
  createFolder,
  deleteFolder,
  deleteResource,
  emptyTrashFolder,
  fetchFolders,
  fetchPinnedResources,
  fetchResource,
  fetchResources,
  fetchTrashResourcesCount,
  initialize,
  pinUnpinResource,
  removeFile,
  updateFolder,
  updatePinnedResources,
  updateResource,
  uploadFiles,
} from "./actions";
import { fetchConnections } from "./externalResources/actions";
import { externalResourcesSaga } from "./externalResources/sagas";
import messages from "./messages";
import ResourcesViewState from "./ResourcesViewState";
import styles from "./saga.module.scss";
import { getPinnedResources, getResourcesViewState } from "./selectors";

const getIsSharedWithMeFilterActive = (filter: FilterModel, currentUserId: User["id"]) => {
  const sharedCondition = getSimpleConditionForField(filter, ResourceFieldName.SHARED_WITH);
  const ownerCondition = getSimpleConditionForField(filter, ResourceFieldName.OWNER);

  if (
    sharedCondition?.operator === FilterOperator.CONTAINS &&
    ownerCondition?.operator === FilterOperator.NOT_EQUAL
  ) {
    const sharedWithUserIds = sharedCondition.value;
    return (
      Array.isArray(sharedWithUserIds) &&
      sharedWithUserIds.length === 1 &&
      sharedWithUserIds[0] === currentUserId
    );
  }

  return false;
};

const showStorageErrorNotification = (intl: IntlShape, isMember: boolean) =>
  notification.error({
    description: (
      <div>
        {intl.formatMessage(messages.storageLimitErrorDescription, {
          isMember,
        })}
        {!isMember && (
          <div className={styles.upgrade}>
            <Button href={STORAGE_UPGRADE_LINK} size="small" target="_blank" type="primary">
              {intl.formatMessage(messages.upgrade)}
            </Button>
          </div>
        )}
      </div>
    ),
    message: intl.formatMessage(messages.storageLimitError),
  });

export function* onInitialize() {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const currentUserId: User["id"] = yield select(getCurrentUserId);
    const [response, resourcesResponse]: [ListResponse<Folder>, ResourceResponse] = yield all([
      callApi("fetchFolders", orgId, {
        $filters: {
          $and: [{ userIds: { $in: [currentUserId] } }],
        },
        $limit: 10000,
        $order: "updatedAt",
      }),
      callApi("fetchResources", orgId, {
        $limit: 0,
      }), // required for storageUsed for current user
    ]);

    yield put(fetchPinnedResources.request());
    yield put(fetchConnections.request());

    yield put(
      initialize.success({
        folders: response.data,
        resources: resourcesResponse.data,
        storageUsed: resourcesResponse.storageUsed ?? 0,
      })
    );
  } catch (error) {
    yield put(initialize.failure());
    yield put(handleError({ error }));
  }
}

export function* onFetchFolders() {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const currentUserId: User["id"] = yield select(getCurrentUserId);
    const response: ListResponse<Folder> = yield callApi("fetchFolders", orgId, {
      $filters: {
        $and: [{ userIds: { $in: [currentUserId] } }],
      },
      $limit: 10000,
      $order: "updatedAt",
    });

    yield put(fetchFolders.success(response.data));
  } catch (error) {
    yield put(fetchFolders.failure());
    yield put(handleError({ error }));
  }
}

export function* onFetchResources() {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const { filter, folderId, range, sort }: ResourcesViewState =
      yield select(getResourcesViewState);
    const { pathname }: RouterLocation<any> = yield select(getLocation);
    const currentUserId: User["id"] = yield select(getCurrentUserId);

    const isTrash = pathname.includes(TRASH_FOLDER);

    const sharedWithMeActive = getIsSharedWithMeFilterActive(filter, currentUserId);
    let updatedFilter = upsertSimpleConditions(
      filter,
      createSimpleCondition(ResourceFieldName.FOLDER, FilterOperator.EQUALS, folderId)
    );
    if (isTrash) {
      updatedFilter = upsertSimpleConditions(
        updatedFilter,
        createSimpleCondition(ResourceFieldName.OWNER, FilterOperator.EQUALS, currentUserId)
      );
      updatedFilter = upsertSimpleConditions(
        updatedFilter,
        createSimpleCondition(ResourceFieldName.TRASHED_AT, FilterOperator.NOT_EMPTY)
      );
    }

    if (sharedWithMeActive) {
      updatedFilter = removeSimpleConditionForFields(updatedFilter, ResourceFieldName.SHARED_WITH);
    }

    const platformFilter = convertToPlatformFilterModel(updatedFilter, [], resourceFieldModel);

    if (sharedWithMeActive) {
      const sharedWithFilter = {
        $or: [
          {
            "folder.userIds": { $in: [currentUserId] },
          },
          {
            [ResourceFieldName.SHARED_WITH]: { $in: [currentUserId] },
          },
        ],
      };
      platformFilter.$and?.push(sharedWithFilter);
    }

    const response: ResourceResponse = yield callApi("fetchResources", orgId, {
      $filters: {
        ...platformFilter,
        includeTrash: isTrash,
      },
      $limit: range.endRow - range.startRow,
      $offset: range.startRow,
      $order: convertToPlatformSortModel(sort),
    });

    yield put(
      fetchResources.success({
        resources: response.data,
        storageUsed: response.storageUsed ?? 0,
        totalResources: response.total,
      })
    );
  } catch (error) {
    yield put(fetchResources.failure());
    yield put(handleError({ error }));
  }
}

export function* onFetchResource({
  payload: { id, failureCallback, options, successCallback },
}: ReturnType<typeof fetchResource.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const resource: Resource = yield callApi("fetchResource", orgId, id, options);

    if (successCallback) {
      yield call(successCallback, resource);
    }
    yield put(fetchResource.success());
  } catch (error) {
    if (failureCallback && error instanceof ApiError) {
      yield call(failureCallback, error);
    }
    yield put(fetchResource.failure());
    yield put(handleError({ error }));
  }
}

export function* onUploadFiles({
  payload: { callback, files },
}: ReturnType<typeof uploadFiles.request>) {
  try {
    const org: Organization = yield select(getOrganization);

    const fileGroupId = uuid.v4();
    const responses: SettleResult<RawFile>[] = yield allSettled(
      files.map((file) =>
        callApi("createFile", org.id, file, false, undefined, undefined, {
          headers: {
            "x-mmc-file-group-id": fileGroupId,
            "x-mmc-file-type": "resource-file",
          },
        })
      )
    );

    const fileList: FileListItem[] = responses.map((response, index) => ({
      file: files[index],
      uploading: false,
      ...(response.error
        ? { errored: true, errorMessage: String(response.result) }
        : { errored: false, uploadedFile: response.result }),
    }));
    if (callback) {
      yield call(callback, fileList);
    }

    const erroredFiles = responses.filter(({ error }) => error);

    if (erroredFiles.length) {
      const isMember: boolean = yield select(isCurrentUserMember);

      const error = erroredFiles[0].result;
      if (isApiError(error)) {
        const validationErrors = getValidationErrors(error);
        const intl = i18nService.getIntl();
        if (intl) {
          if (validationErrors[0]?.code === "v0913") {
            showStorageErrorNotification(intl, isMember);
          }
        }
      }
    }

    yield put(uploadFiles.success(fileList));
  } catch (error) {
    if (callback) {
      yield call(
        callback,
        files.map((file) => ({ errored: true, file, uploading: false }))
      );
    }
    yield put(uploadFiles.failure());
    yield put(handleError({ error }));
  }
}

export function* onRemoveFile({ payload: { id } }: ReturnType<typeof removeFile.request>) {
  try {
    const org: Organization = yield select(getOrganization);
    yield callApi("deleteFile", org.id, id);
    yield put(removeFile.success(id));
  } catch (error) {
    yield put(removeFile.failure());
    yield put(handleError({ error }));
  }
}

export function* onBulkCreateResources({
  payload: { files, folderId, isPublic, linkName, successCallback, url, userIds },
}: ReturnType<typeof bulkCreateResources.request>) {
  try {
    const org: Organization = yield select(getOrganization);
    const { range }: ResourcesViewState = yield select(getResourcesViewState);
    let payload: ResourcesCreatePayload | undefined;

    if (linkName && url) {
      payload = {
        resourceIds: [
          {
            folder: folderId ? { id: folderId } : undefined,
            name: linkName,
            url,
            userIds,
          },
        ],
      };
    } else if (files.length) {
      payload = {
        resourceIds: files.map(({ id, name }) => ({
          file: { id },
          folder: folderId ? { id: folderId } : undefined,
          isPublic,
          name,
          userIds,
        })),
      };
    }

    if (payload) {
      const response: [Resource[], BulkApiError[]] = yield callApi(
        "bulkCreateResources",
        org.id,
        payload
      );

      // Assuming all resources will fail to create if they do
      if (response[1].length) {
        yield put(bulkCreateResources.failure());
        yield put(handleError({ error: response[1][1].errors }));
      } else {
        yield put(notifyAboutChanges({ added: response[0], entityType: EntityType.RESOURCE }));

        yield put(
          fetchResources.request({ range: { endRow: range.endRow - range.startRow, startRow: 0 } })
        );

        const intl = i18nService.getIntl();
        if (intl) {
          notification.success({
            message: intl.formatMessage(messages.resourcesCreateSuccess, {
              count: linkName && url ? 1 : files.length,
            }),
          });
        }

        if (successCallback) {
          yield call(successCallback);
        }

        yield put(bulkCreateResources.success());
      }
    }
  } catch (error) {
    const isMember: boolean = yield select(isCurrentUserMember);

    yield put(bulkCreateResources.failure());
    const validationErrors = getValidationErrors(error);
    const intl = i18nService.getIntl();
    if (intl) {
      if (validationErrors[0]?.code === "v0913") {
        showStorageErrorNotification(intl, isMember);
      } else {
        notification.error({
          description: intl.formatMessage(messages.errorDescription),
          message: intl.formatMessage(messages.resourcesCreateError, {
            count: linkName && url ? 1 : files.length,
          }),
        });
      }
    } else {
      yield put(handleError({ error }));
    }
  }
}

export function* onCreateFolder({
  payload: { callback, name, parentFolderId },
}: ReturnType<typeof createFolder.request>) {
  try {
    const org: Organization = yield select(getOrganization);
    const folder: Folder = yield callApi("createFolder", org.id, { name, parentFolderId });

    if (callback) {
      yield call(callback, folder.id);
    }
    yield put(createFolder.success(folder));
  } catch (error) {
    yield put(createFolder.failure());
    yield put(handleError({ error }));
  }
}

export function* onDeleteFolder({
  payload: { id, callback },
}: ReturnType<typeof deleteFolder.request>) {
  try {
    const org: Organization = yield select(getOrganization);
    yield callApi("deleteFolder", org.id, id);

    if (callback) {
      yield call(callback);
    }

    yield put(deleteFolder.success(id));
  } catch (error) {
    yield put(deleteFolder.failure());

    const intl = i18nService.getIntl();
    if (intl) {
      notification.error({
        description: intl.formatMessage(messages.errorDescription),
        message: intl.formatMessage(messages.folderDeleteError),
      });
    }
    yield put(handleError({ error }));
  }
}

export function* onBulkDeleteFolders({
  payload: { callback, ids },
}: ReturnType<typeof bulkDeleteFolders.request>) {
  try {
    const org: Organization = yield select(getOrganization);
    yield callApi("bulkDeleteFolders", org.id, ids);

    if (callback) {
      yield call(callback);
    }

    const intl = i18nService.getIntl();
    if (intl) {
      notification.success({
        message: intl.formatMessage(messages.folderBulkDeleteSuccess, { total: ids.length }),
      });
    }

    yield put(bulkDeleteFolders.success(ids));
  } catch (error) {
    yield put(bulkDeleteFolders.failure());

    const intl = i18nService.getIntl();
    if (intl) {
      notification.error({
        description: intl.formatMessage(messages.errorDescription),
        message: intl.formatMessage(messages.folderBulkDeleteError, { total: ids.length }),
      });
    }
    yield put(handleError({ error }));
  }
}

export function* onBulkEditFolders({
  payload: { callback, folders },
}: ReturnType<typeof bulkEditFolders.request>) {
  try {
    const org: Organization = yield select(getOrganization);
    yield callApi("bulkEditFolders", org.id, folders);

    if (callback) {
      yield call(callback);
    }

    yield put(fetchFolders.request());
    yield put(bulkEditFolders.success());
  } catch (error) {
    yield put(bulkEditFolders.failure());

    const intl = i18nService.getIntl();
    if (intl) {
      notification.error({
        description: intl.formatMessage(messages.errorDescription),
        message: intl.formatMessage(messages.folderBulkEditError, { total: folders.length }),
      });
    }
    yield put(handleError({ error }));
  }
}

export function* onUpdateFolder({
  payload: { callback, folder },
}: ReturnType<typeof updateFolder.request>) {
  try {
    const org: Organization = yield select(getOrganization);
    const updatedFolder: Folder = yield callApi("updateFolder", org.id, folder);

    if (callback) {
      yield call(callback);
    }

    yield put(updateFolder.success(updatedFolder));
  } catch (error) {
    yield put(updateFolder.failure());

    const intl = i18nService.getIntl();
    if (intl) {
      notification.error({
        description: intl.formatMessage(messages.errorDescription),
        message: intl.formatMessage(messages.folderUpdateError),
      });
    }
    yield put(handleError({ error }));
  }
}

export function* onUpdateResource({
  payload: { resource, restore, successCallback, toTrash },
}: ReturnType<typeof updateResource.request>) {
  try {
    const org: Organization = yield select(getOrganization);
    const pinnedResourceIds: Resource["id"][] = yield select(getPinnedResourceIds);

    const updatedResource: Resource = yield callApi("updateResource", org.id, resource, {
      restore,
      toTrash,
    });

    if (restore) {
      yield put(
        notifyAboutChanges({ entityType: EntityType.RESOURCE, restored: [updatedResource] })
      );
    } else if (toTrash) {
      yield put(
        notifyAboutChanges({ deletedIds: [updatedResource.id], entityType: EntityType.RESOURCE })
      );
    } else {
      yield put(
        notifyAboutChanges({ entityType: EntityType.RESOURCE, updated: [updatedResource] })
      );
    }

    if (successCallback) {
      yield call(successCallback);
    }

    if (pinnedResourceIds.includes(resource.id)) {
      yield put(fetchPinnedResources.request());
    }

    yield put(fetchResources.request({}));
    if (restore || toTrash) {
      yield put(fetchTrashResourcesCount.request());
    }

    yield put(updateResource.success());
  } catch (error) {
    yield put(updateResource.failure());
    const intl = i18nService.getIntl();
    if (intl) {
      notification.error({
        description: intl.formatMessage(messages.errorDescription),
        message: intl.formatMessage(messages.updateError),
      });
    }
    yield put(handleError({ error }));
  }
}

export function* onDeleteResource({
  payload: { id, isTrash, name },
}: ReturnType<typeof deleteResource.request>) {
  try {
    const org: Organization = yield select(getOrganization);
    const pinnedResourceIds: Resource["id"][] = yield select(getPinnedResourceIds);

    if (isTrash) {
      yield callApi("deleteResource", org.id, id);
    } else {
      yield callApi("updateResource", org.id, { id }, { toTrash: true });

      yield put(notifyAboutChanges({ deletedIds: [id], entityType: EntityType.RESOURCE }));

      if (pinnedResourceIds.includes(id)) {
        yield put(pinUnpinResource.request({ ids: [id], unpin: true }));
      }
    }

    const intl = i18nService.getIntl();
    if (intl) {
      notification.success({
        message: intl.formatMessage(messages.resourceDelete, { isTrash, resourceName: name }),
      });
    }

    yield put(fetchResources.request({}));
    yield put(fetchTrashResourcesCount.request());

    yield put(deleteResource.success());
  } catch (error) {
    yield put(deleteResource.failure());
    const intl = i18nService.getIntl();
    if (intl) {
      notification.error({
        description: intl.formatMessage(messages.errorDescription),
        message: intl.formatMessage(messages.resourceDeleteError, { isTrash }),
      });
    }
    yield put(handleError({ error }));
  }
}

export function* onEmptyTrashFolder() {
  try {
    const org: Organization = yield select(getOrganization);
    yield callApi("emptyTrashFolder", org.id);
    yield put(fetchResources.request({}));

    const intl = i18nService.getIntl();
    if (intl) {
      notification.success({
        message: intl.formatMessage(messages.emptyTrashSuccess),
      });
    }

    yield put(fetchTrashResourcesCount.success(0));

    yield put(emptyTrashFolder.success());
  } catch (error) {
    yield put(deleteResource.failure());

    const intl = i18nService.getIntl();
    if (intl) {
      notification.error({
        description: intl.formatMessage(messages.errorDescription),
        message: intl.formatMessage(messages.emptyTrashError),
      });
    }
    yield put(handleError({ error }));
  }
}

export function* onBulkDeleteResources({
  payload: { callback, ids, isTrash },
}: ReturnType<typeof bulkDeleteResources.request>) {
  try {
    const org: Organization = yield select(getOrganization);
    const pinnedResourceIds: Resource["id"][] = yield select(getPinnedResourceIds);
    const { range }: ResourcesViewState = yield select(getResourcesViewState);

    if (isTrash) {
      yield callApi("bulkDeleteResources", org.id, ids);
    } else {
      yield callApi(
        "bulkEditResources",
        org.id,
        ids.map((id) => ({ id })),
        { toTrash: true }
      );

      yield put(notifyAboutChanges({ deletedIds: ids, entityType: EntityType.RESOURCE }));
    }

    const pinnedIds = ids.filter((id) => pinnedResourceIds.includes(id));
    if (pinnedIds.length) {
      yield put(pinUnpinResource.request({ ids: pinnedIds, unpin: true }));
    }

    if (callback) {
      yield call(callback);
    }

    const intl = i18nService.getIntl();
    if (intl) {
      notification.success({
        message: intl.formatMessage(messages.bulkDelete, { isTrash, total: ids.length }),
      });
    }

    yield put(
      fetchResources.request({ range: { endRow: range.endRow - range.startRow, startRow: 0 } })
    );
    yield put(fetchTrashResourcesCount.request());

    yield put(bulkDeleteResources.success());
  } catch (error) {
    yield put(bulkDeleteResources.failure());

    const intl = i18nService.getIntl();
    if (intl) {
      notification.error({
        description: intl.formatMessage(messages.errorDescription),
        message: intl.formatMessage(messages.bulkDeleteError, { isTrash, total: ids.length }),
      });
    }
    yield put(handleError({ error }));
  }
}

export function* onBulkEditResources({
  payload: { callback, resources, restore, toTrash },
}: ReturnType<typeof bulkEditResources.request>) {
  try {
    const org: Organization = yield select(getOrganization);
    const pinnedResourceIds: Resource["id"][] = yield select(getPinnedResourceIds);

    const updatedResources: Resource[] = yield callApi("bulkEditResources", org.id, resources, {
      restore,
      toTrash,
    });

    if (restore) {
      yield put(
        notifyAboutChanges({ entityType: EntityType.RESOURCE, restored: updatedResources })
      );
    } else if (toTrash) {
      yield put(
        notifyAboutChanges({
          deletedIds: resources.map((resource) => resource.id),
          entityType: EntityType.RESOURCE,
        })
      );
    }

    if (callback) {
      yield call(callback);
    }

    if (resources.some(({ id }) => pinnedResourceIds.includes(id))) {
      yield put(fetchPinnedResources.request());
    }

    yield put(fetchResources.request({}));

    if (restore || toTrash) {
      yield put(fetchTrashResourcesCount.request());
    }

    yield put(bulkEditResources.success());
  } catch (error) {
    yield put(bulkEditResources.failure());
    yield put(handleError({ error }));
  }
}

export function* onFetchPinnedResources({
  payload,
}: ReturnType<typeof fetchPinnedResources.request>) {
  try {
    const org: Organization = yield select(getOrganization);
    const resourceIds: Resource["id"][] = yield select(getPinnedResourceIds);
    const response: ResourceResponse = yield callApi("fetchResources", org.id, {
      $filters: { $and: [{ id: { $in: payload ?? resourceIds } }] },
      $limit: 1000,
    });

    const resourceMap = new Map(response.data.map((resource) => [resource.id, resource]));
    const resources = (payload ?? resourceIds)
      .filter((id) => resourceMap.has(id))
      .map((id) => resourceMap.get(id)!);

    yield put(fetchPinnedResources.success(resources));
  } catch (error) {
    yield put(fetchPinnedResources.failure());
    yield put(handleError({ error }));
  }
}

export function* onPinUnpinResource({
  payload: { ids, unpin },
}: ReturnType<typeof pinUnpinResource.request>) {
  try {
    const org: Organization = yield select(getOrganization);
    const resourceIds: Resource["id"][] = yield select(getPinnedResourceIds);
    const resources: Resource[] = yield select(getPinnedResources);

    const updatedIds = unpin
      ? resourceIds.filter((resourceId) => !ids.includes(resourceId))
      : [...ids, ...resourceIds];

    yield put(
      updateMetadata.request({
        pinnedResourceIds: updatedIds,
      })
    );

    if (unpin) {
      yield put(
        pinUnpinResource.success(resources.filter((resource) => !ids.includes(resource.id)))
      );
    } else {
      const response: ResourceResponse = yield callApi("fetchResources", org.id, {
        $filters: { $and: [{ id: { $in: ids } }] },
      });
      yield put(pinUnpinResource.success([...response.data, ...resources]));
    }

    const intl = i18nService.getIntl();
    if (intl) {
      notification.success({
        message: intl.formatMessage(messages.pinUnpinSuccess, { unpin }),
      });
    }
  } catch (error) {
    yield put(pinUnpinResource.failure());

    const intl = i18nService.getIntl();
    if (intl) {
      notification.error({
        description: intl.formatMessage(messages.errorDescription),
        message: intl.formatMessage(messages.pinUnpinError, { unpin }),
      });
    }
    yield put(handleError({ error }));
  }
}

export function* onUpdatePinnedResources({
  payload: { callback, ids },
}: ReturnType<typeof updatePinnedResources.request>) {
  try {
    yield put(
      updateMetadata.request({
        pinnedResourceIds: ids,
      })
    );

    yield put(fetchPinnedResources.request(ids));

    if (callback) {
      yield call(callback);
    }

    yield put(updatePinnedResources.success());
  } catch (error) {
    yield put(updatePinnedResources.failure());
    yield put(handleError({ error }));
  }
}

export function* onFetchTrashResourcesCount() {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const response: ResourceResponse = yield callApi("fetchResources", orgId, {
      $filters: {
        $and: [
          {
            trashedAt: {
              $ne: null,
            },
          },
        ],
        includeTrash: true,
      },
      $limit: 0,
    });

    yield put(fetchTrashResourcesCount.success(response.total));
  } catch (error) {
    yield put(fetchTrashResourcesCount.failure());
    yield put(handleError({ error }));
  }
}

export function* resourcesSaga() {
  yield fork(externalResourcesSaga);
  yield takeLatest(initialize.request, onInitialize);
  yield takeLatest(fetchFolders.request, onFetchFolders);
  yield takeLatest(fetchResources.request, onFetchResources);
  yield takeLatest(fetchResource.request, onFetchResource);
  yield takeEvery(uploadFiles.request, onUploadFiles);
  yield takeEvery(removeFile.request, onRemoveFile);
  yield takeLatest(bulkCreateResources.request, onBulkCreateResources);
  yield takeLatest(bulkDeleteFolders.request, onBulkDeleteFolders);
  yield takeLatest(bulkEditFolders.request, onBulkEditFolders);
  yield takeLatest(createFolder.request, onCreateFolder);
  yield takeLatest(updateFolder.request, onUpdateFolder);
  yield takeLatest(deleteFolder.request, onDeleteFolder);
  yield takeLatest(updateResource.request, onUpdateResource);
  yield takeLatest(deleteResource.request, onDeleteResource);
  yield takeLatest(emptyTrashFolder.request, onEmptyTrashFolder);
  yield takeLatest(bulkDeleteResources.request, onBulkDeleteResources);
  yield takeLatest(bulkEditResources.request, onBulkEditResources);
  yield takeLatest(fetchPinnedResources.request, onFetchPinnedResources);
  yield takeEvery(pinUnpinResource.request, onPinUnpinResource);
  yield takeLatest(updatePinnedResources.request, onUpdatePinnedResources);
  yield takeLatest(
    [initialize.request, fetchTrashResourcesCount.request],
    onFetchTrashResourcesCount
  );
}
